import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import {
  fetchReceipts,
  ReceiptStatusBadge,
  selectReceipts,
  selectReceiptsStatus,
} from '@features/receipts';
import type { Receipt } from '@features/receipts';
import { formatConfidence, formatDate } from '@shared/lib/format';
import { Card, CardContent, CardHeader } from '@shared/ui/card/Card';
import { EmptyState } from '@shared/ui/empty-state/EmptyState';
import { Icon } from '@shared/ui/icon/Icon';

/** Génère des insights pédagogiques à partir des données réelles. */
function buildInsights(receipts: Receipt[]): string[] {
  const insights: string[] = [];
  if (receipts.length === 0) return insights;

  const low = receipts.filter((r) => r.confidence < 0.85).length;
  if (low > 0) {
    insights.push(
      `${low} document${low > 1 ? 's' : ''} avec une confiance inférieure à 85 % : une revue manuelle est recommandée avant validation.`,
    );
  } else {
    insights.push('Tous les documents récents ont été extraits avec une confiance élevée.');
  }

  const avg = receipts.reduce((sum, r) => sum + r.confidence, 0) / receipts.length;
  insights.push(
    `Confiance moyenne d'extraction de ${formatConfidence(avg)} sur ${receipts.length} document${receipts.length > 1 ? 's' : ''}.`,
  );

  const categories = new Set(receipts.map((r) => r.category));
  insights.push(
    `${categories.size} catégorie${categories.size > 1 ? 's' : ''} détectée${categories.size > 1 ? 's' : ''} automatiquement.`,
  );

  return insights;
}

/**
 * Page « AI Analysis » : documents analysés, scores de confiance, statut
 * d'extraction et insights. L'IA est présentée comme une aide à la décision,
 * jamais comme une source de vérité absolue.
 */
export function AiPage() {
  const dispatch = useAppDispatch();
  const receipts = useAppSelector(selectReceipts);
  const receiptsStatus = useAppSelector(selectReceiptsStatus);

  // Charge les receipts depuis l'API si ce n'est pas déjà fait.
  useEffect(() => {
    if (receiptsStatus === 'idle') {
      void dispatch(fetchReceipts());
    }
  }, [dispatch, receiptsStatus]);

  const insights = buildInsights(receipts);
  const recent = receipts.slice(0, 8);

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Analyses IA</h1>
        <p className="text-sm text-slate-500">
          Suivez la qualité des extractions et gardez la main sur chaque donnée.
        </p>
      </div>

      {/* Bannière : l'IA comme aide à la décision */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="flex items-start gap-3">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <Icon name="sparkles" size={18} />
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-emerald-900">
              Une aide à la décision, pas une source de vérité
            </p>
            <p className="text-sm text-emerald-800">
              L'IA propose des données extraites automatiquement. Vous restez responsable de la
              validation : chaque champ est vérifiable et modifiable avant enregistrement.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Documents récemment analysés */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Analysés récemment"
            subtitle="Documents traités par l'IA, avec score et statut"
          />
          <CardContent>
            {recent.length === 0 ? (
              <EmptyState
                icon="sparkles"
                title="Aucune analyse pour le moment"
                description="Importez un ticket et lancez l'analyse pour voir les résultats ici."
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {recent.map((receipt) => (
                  <li key={receipt.id} className="flex items-center justify-between gap-4 py-3">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {receipt.merchant}
                      </span>
                      <span className="text-xs text-slate-400">
                        {receipt.fileName} · {formatDate(receipt.date)}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm font-medium text-slate-700">
                        {formatConfidence(receipt.confidence)}
                      </span>
                      <ReceiptStatusBadge status={receipt.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Insights IA */}
        <Card>
          <CardHeader title="Insights IA" subtitle="Ce que l'IA observe sur vos documents" />
          <CardContent>
            {insights.length === 0 ? (
              <p className="text-sm text-slate-500">
                Les insights apparaîtront après vos premières analyses.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {insights.map((insight) => (
                  <li key={insight} className="flex items-start gap-2.5">
                    <Icon name="sparkles" size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span className="text-sm text-slate-600">{insight}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
